import { ApiService, type ApiRequestConfig } from "./api.service";
import { uploadToPresignedUrl, type PresignedUploadOptions } from "./presigned-upload";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type {
  TWorkItemImportCommitResult,
  TWorkItemImportDefinition,
  TWorkItemImportDryRunResult,
  TWorkItemImportRowPayload,
} from "@/types/work-item-import";
import type {
  TCreateProjectFromTemplateOverrides,
  TCreateProjectTemplatePayload,
  TCreatedProjectFromTemplate,
  TProjectTemplateDetail,
  TProjectTemplateListItem,
  TResolvedTemplateCustomField,
  TTemplateCustomFieldEntry,
  TUpdateProjectTemplatePayload,
  TWorkitemTemplateAttachment,
  TWorkitemTemplateAttachmentUploadResponse,
} from "@/types/project-template";

export interface TUpdateWorkitemTemplateCustomFieldsResponse {
  id: string;
  custom_fields: TTemplateCustomFieldEntry[];
  custom_fields_schema: TResolvedTemplateCustomField[];
}

export type TPickableProjectTemplate = Pick<
  TProjectTemplateDetail,
  | "id"
  | "template_name"
  | "description"
  | "privacy"
  | "project_lead"
  | "lead_ids"
  | "category"
  | "delivery_teams_label"
  | "start_date"
  | "end_date"
  | "custom_fields_schema"
>;

export interface IProjectTemplatesPage {
  results: TProjectTemplateListItem[];
  next_cursor: string;
  prev_cursor: string;
  next_page_results: boolean;
  prev_page_results: boolean;
  total_count: number;
  count: number;
}

export interface IProjectTemplatesListQuery {
  cursor?: string | null;
  q?: string;
  privacy?: number[];
  order_by?: string;
  per_page?: number;
  /**
   * Narrow to the templates a team has access to — the team-settings Project
   * Templates tab (#2926). On this path the server also fills `can_edit` on
   * every row (batch-computed); without it `can_edit` is `null` by contract.
   */
  team?: string;
}

export class ProjectTemplateService extends ApiService {
  async listPickableTemplates(
    workspaceSlug: string,
    config?: ApiRequestConfig,
  ): Promise<TPickableProjectTemplate[]> {
    return this.get(`/api/workspaces/${workspaceSlug}/project-templates/pickable/`, config);
  }

  /** Paginated templates fetch for the settings list (server filters/sort +
   *  cursor pagination). The pickers read ``listPickableTemplates``. */
  async listTemplatesPage(
    workspaceSlug: string,
    options: IProjectTemplatesListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<IProjectTemplatesPage> {
    const params = new URLSearchParams();
    params.set("per_page", String(options.per_page ?? 50));
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.q) params.set("q", options.q);
    if (options.order_by) params.set("order_by", options.order_by);
    for (const p of options.privacy ?? []) params.append("privacy", String(p));
    if (options.team) params.set("team", options.team);
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/templates/?${params.toString()}`,
      config,
    );
  }

  /**
   * Export the Templates list as CSV, scoped to the same filters/sort as the
   * list (`options`) and, when `body.ids` is set, restricted to those rows.
   * Returns the raw Response so the caller can stream the CSV (200) or poll the
   * async token (202).
   */
  async exportTemplates(
    workspaceSlug: string,
    body: { ids?: string[]; columns?: string[] },
    options: { q?: string; privacy?: number[]; order_by?: string } = {},
  ): Promise<Response> {
    const params = new URLSearchParams();
    if (options.q) params.set("q", options.q);
    if (options.order_by) params.set("order_by", options.order_by);
    for (const p of options.privacy ?? []) params.append("privacy", String(p));
    const qs = params.toString();
    return this.postRaw(
      `/api/workspaces/${workspaceSlug}/export-templates/${qs ? `?${qs}` : ""}`,
      body,
    );
  }

  async getExportStatus(workspaceSlug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${workspaceSlug}/export-status/${token}/`);
  }

  async getTemplate(
    workspaceSlug: string,
    id: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectTemplateDetail> {
    return this.get(`/api/workspaces/${workspaceSlug}/projects/templates/${id}/`, config);
  }

  async createTemplate(
    workspaceSlug: string,
    data: TCreateProjectTemplatePayload,
  ): Promise<TProjectTemplateDetail> {
    return this.post(`/api/workspaces/${workspaceSlug}/projects/templates/`, data);
  }

  async updateTemplate(
    workspaceSlug: string,
    id: string,
    data: TUpdateProjectTemplatePayload,
  ): Promise<TProjectTemplateDetail> {
    return this.patch(`/api/workspaces/${workspaceSlug}/projects/templates/${id}/`, data);
  }

  /**
   * Copy a template (#2926). Returns the new template; the server suffixes the
   * name with " (Copy)" and deliberately does NOT carry the source's Team Access
   * over — an inherited audience is one the copy's creator never chose.
   *
   * `teamId` is the team tab the copy was made from (GH-4093). The server scopes
   * the copy to it, which both makes the copy sole-owned — so the duplicating
   * team lead can edit it — and is what authorizes the POST at all: without a
   * team the create gate falls back to the workspace scope, which only
   * admin / powerUser hold, so a lead's duplicate 403s. Omitted from workspace
   * Settings, where the copy is deliberately workspace-wide ("All Teams").
   */
  async duplicateTemplate(
    workspaceSlug: string,
    id: string,
    teamId?: string,
  ): Promise<TProjectTemplateDetail> {
    return this.post(
      `/api/workspaces/${workspaceSlug}/projects/templates/${id}/duplicate/`,
      teamId ? { team: teamId } : {},
    );
  }

  async deleteTemplate(workspaceSlug: string, id: string): Promise<void> {
    await this.delete(`/api/workspaces/${workspaceSlug}/projects/templates/${id}/`);
  }

  async createProjectFromTemplate(
    workspaceSlug: string,
    id: string,
    overrides: TCreateProjectFromTemplateOverrides,
  ): Promise<TCreatedProjectFromTemplate> {
    // Converting a REQUEST goes through the request-first route (NG-134), which
    // is authorized by the caller's relationship to the request in its path
    // rather than by workspace-wide `project.create` — that is what lets a
    // standard-user request owner plan work from their own request. The
    // template-first route stays for a plain create-from-template, where there is
    // no request to authorize against.
    const { intake_submission_id: submissionId, ...rest } = overrides;
    if (submissionId) {
      return this.post(
        `/api/workspaces/${workspaceSlug}/intake/submissions/${submissionId}/convert/`,
        { ...rest, template_id: id },
      );
    }
    return this.post(`/api/workspaces/${workspaceSlug}/projects/templates/${id}/use/`, overrides);
  }

  async updateWorkitemTemplateCustomFields(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    customFields: TTemplateCustomFieldEntry[],
  ): Promise<TUpdateWorkitemTemplateCustomFieldsResponse> {
    return this.patch(
      `/api/workspaces/${workspaceSlug}/projects/templates/${templateId}/workitems/${workitemId}/custom-fields/`,
      { custom_fields: customFields },
    );
  }

  private workitemAttachmentsUrl(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
  ): string {
    return `/api/workspaces/${workspaceSlug}/projects/templates/${templateId}/workitems/${workitemId}/attachments/`;
  }

  async listWorkitemTemplateAttachments(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    config?: ApiRequestConfig,
  ): Promise<TWorkitemTemplateAttachment[]> {
    return this.get(this.workitemAttachmentsUrl(workspaceSlug, templateId, workitemId), config);
  }

  /** Presign → upload-to-storage → finalize, mirroring the runtime work-item
   *  flow. Sends ``type`` (not ``content_type``) so the BE stores the real
   *  content type. */
  async uploadWorkitemTemplateAttachment(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    file: File,
    options?: PresignedUploadOptions,
  ): Promise<TWorkitemTemplateAttachment> {
    const base = this.workitemAttachmentsUrl(workspaceSlug, templateId, workitemId);
    const { upload_data, asset_id } = await this.post<TWorkitemTemplateAttachmentUploadResponse>(
      base,
      { name: file.name, size: file.size, type: file.type },
    );

    await uploadToPresignedUrl(upload_data, file, options);

    return this.patch(`${base}${asset_id}/`, {});
  }

  async deleteWorkitemTemplateAttachment(
    workspaceSlug: string,
    templateId: string,
    workitemId: string,
    attachmentId: string,
  ): Promise<void> {
    return this.delete(
      `${this.workitemAttachmentsUrl(workspaceSlug, templateId, workitemId)}${attachmentId}/`,
    );
  }

  // --- Work-items bulk import (NG-3509) -------------------------------------------------
  // The template counterparts of WorkItemService's project import methods (NG-3504),
  // against the NG-3506/3508 template endpoints. The response shapes are identical
  // (the backend reuses the same serializers), so the project types are reused.

  /** The field catalogue the import wizard maps an uploaded CSV/XLSX against. */
  async getTemplateWorkItemImportDefinition(
    workspaceSlug: string,
    templateId: string,
  ): Promise<TWorkItemImportDefinition> {
    return this.get<TWorkItemImportDefinition>(
      `/api/workspaces/${workspaceSlug}/projects/templates/${templateId}/workitems/import-definition/`,
    );
  }

  /** The Review step's preview: the real resolution, read-only. */
  async dryRunTemplateWorkItemImport(
    workspaceSlug: string,
    templateId: string,
    rows: TWorkItemImportRowPayload[],
  ): Promise<TWorkItemImportDryRunResult> {
    return this.post<TWorkItemImportDryRunResult>(
      `/api/workspaces/${workspaceSlug}/projects/templates/${templateId}/workitems/import/dry-run/`,
      { rows },
    );
  }

  /** Commit the import: creates one WorkitemTemplate per valid row, server-side. */
  async commitTemplateWorkItemImport(
    workspaceSlug: string,
    templateId: string,
    rows: TWorkItemImportRowPayload[],
  ): Promise<TWorkItemImportCommitResult> {
    return this.post<TWorkItemImportCommitResult>(
      `/api/workspaces/${workspaceSlug}/projects/templates/${templateId}/workitems/import/`,
      { rows },
    );
  }
}
